/**
 * Analytics — GA4 실측 분석 대시보드 (전용 페이지)
 *
 * 기간 필터 + 요약 KPI + 4탭(트래픽 / 매출 시점 / 유입 소스 / 행동).
 * 데이터 소스: GET /api/analytics/{overview,timeseries,timing,sources,behavior} (GA4 Data API).
 * 기간 로직은 공용 헬퍼 lib/dateRange.ts 재사용. 탭별 lazy fetch 로 GA4 quota 절감.
 */
import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router'
import { useApiFetch } from '@/hooks/useApiFetch'
import {
  Card,
  DateRangePicker,
  Spinner,
  Alert,
  StatCard,
  Table,
  Tabs,
  LineChart,
  BarChart,
  DonutChart,
  CHART_COLORS,
  CHART_PALETTE,
  formatNumber,
  formatCurrency,
  formatPercent,
  type TableColumn,
} from '@/ui'
import { resolveRange, SHARED_RANGE_PRESETS } from '@/lib/dateRange'
import { HeatmapGrid } from './analytics/HeatmapGrid'
import { TimingCellDrawer } from './analytics/TimingCellDrawer'
import styles from './Analytics.module.css'

const DAY_LABELS = ['일', '월', '화', '수', '목', '금', '토']

// ── 응답 DTO 타입 (백엔드 lib/analytics/types.ts 와 대응) ──
interface MetricPair {
  current: number
  previous: number
}
interface OverviewDto {
  from: string
  to: string
  sessions: MetricPair
  activeUsers: MetricPair
  newUsers: MetricPair
  screenPageViews: MetricPair
  keyEvents: MetricPair
  transactions: MetricPair
  totalRevenue: MetricPair
  engagementRate: MetricPair
}
interface TimeseriesDto {
  granularity: 'day' | 'hour'
  points: { date: string; sessions: number; activeUsers: number; screenPageViews: number }[]
}
export interface TimingMatrix {
  matrix: number[][]
  hourTotals: number[]
  dayTotals: number[]
  total: number
}
export interface TimingDto {
  revenue: TimingMatrix
  transactions: TimingMatrix
  sessions: TimingMatrix
  activeUsers: TimingMatrix
}
interface SourcesDto {
  channels: { channel: string; sessions: number; activeUsers: number; keyEvents: number; totalRevenue: number }[]
  sourceMediums: { sourceMedium: string; sessions: number; keyEvents: number; totalRevenue: number }[]
}
interface BehaviorDto {
  events: { eventName: string; eventCount: number; totalUsers: number }[]
  pages: { pagePath: string; screenPageViews: number; activeUsers: number; userEngagementDuration: number }[]
}

/** 증감률(%) 표시. previous=0 이면 표시 안 함. '+'/'-' 접두로 StatCard 색상 결정. */
function deltaText(current: number, previous: number): string | undefined {
  if (previous === 0) return undefined
  const pct = ((current - previous) / previous) * 100
  return `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`
}

type TabKey = 'traffic' | 'timing' | 'sources' | 'behavior'

export function Analytics() {
  const [sp, setSp] = useSearchParams()
  const { from, to, preset } = useMemo(() => resolveRange(sp), [sp])

  const tab = (sp.get('tab') as TabKey) || 'traffic'
  const setTab = (k: string) => {
    const next = new URLSearchParams(sp)
    next.set('tab', k)
    setSp(next, { replace: true })
  }

  // 기간 선택 — 프리셋이면 preset 키를, 커스텀 범위면 from/to 를 URL 에 커밋 (tab 유지).
  const handleRangeChange = (
    range: { from: string | null; to: string | null },
    meta: { presetKey: string | null },
  ) => {
    if (meta.presetKey) {
      setSp({ preset: meta.presetKey, tab })
    } else if (range.from && range.to) {
      setSp({ from: range.from, to: range.to, tab })
    }
  }

  const qs = `from=${from}&to=${to}`

  // KPI 는 항상 로드, 탭 데이터는 활성 탭만 lazy fetch
  const overview = useApiFetch<OverviewDto>(`/api/analytics/overview?${qs}`)
  const timeseries = useApiFetch<TimeseriesDto>(tab === 'traffic' ? `/api/analytics/timeseries?${qs}&granularity=day` : null)
  const timing = useApiFetch<TimingDto>(tab === 'timing' ? `/api/analytics/timing?${qs}` : null)
  const sources = useApiFetch<SourcesDto>(tab === 'sources' ? `/api/analytics/sources?${qs}` : null)
  const behavior = useApiFetch<BehaviorDto>(tab === 'behavior' ? `/api/analytics/behavior?${qs}` : null)

  const ov = overview.data

  // 미연동(503) 안내
  const notConfigured = overview.error?.includes('연동') ?? false

  return (
    <div className={styles.root}>
      {/* 기간 필터 */}
      <div className={styles.filterBar}>
        <div className={styles.customRange}>
          <DateRangePicker
            size="sm"
            value={{ from, to }}
            activePresetKey={preset}
            presets={SHARED_RANGE_PRESETS}
            onChange={handleRangeChange}
          />
        </div>
      </div>

      {notConfigured ? (
        <Alert variant="warning">
          GA4가 아직 연동되지 않았습니다. 서비스 계정 자격증명 설정이 필요합니다 (docs/ga4-setup.md).
        </Alert>
      ) : (
        overview.error && <Alert variant="danger">불러오지 못했습니다: {overview.error}</Alert>
      )}

      <Alert variant="info">GA4 집계 특성상 최근 24~48시간 데이터는 변동될 수 있습니다.</Alert>

      {/* KPI 카드 */}
      <div className={styles.kpiGrid}>
        <KpiCard label="세션" value={ov ? formatNumber(ov.sessions.current) : '—'} change={ov ? deltaText(ov.sessions.current, ov.sessions.previous) : undefined} loading={overview.isLoading} />
        <KpiCard label="활성 사용자" value={ov ? formatNumber(ov.activeUsers.current) : '—'} change={ov ? deltaText(ov.activeUsers.current, ov.activeUsers.previous) : undefined} loading={overview.isLoading} />
        <KpiCard label="신규 사용자" value={ov ? formatNumber(ov.newUsers.current) : '—'} change={ov ? deltaText(ov.newUsers.current, ov.newUsers.previous) : undefined} loading={overview.isLoading} />
        <KpiCard label="페이지뷰" value={ov ? formatNumber(ov.screenPageViews.current) : '—'} change={ov ? deltaText(ov.screenPageViews.current, ov.screenPageViews.previous) : undefined} loading={overview.isLoading} />
        <KpiCard label="전환(key events)" value={ov ? formatNumber(ov.keyEvents.current) : '—'} change={ov ? deltaText(ov.keyEvents.current, ov.keyEvents.previous) : undefined} loading={overview.isLoading} />
        <KpiCard label="결제 건수" value={ov ? formatNumber(ov.transactions.current) : '—'} change={ov ? deltaText(ov.transactions.current, ov.transactions.previous) : undefined} loading={overview.isLoading} />
        <KpiCard label="매출" value={ov ? formatCurrency(ov.totalRevenue.current) : '—'} change={ov ? deltaText(ov.totalRevenue.current, ov.totalRevenue.previous) : undefined} loading={overview.isLoading} />
        <KpiCard label="참여율" value={ov ? formatPercent(ov.engagementRate.current * 100) : '—'} loading={overview.isLoading} />
      </div>

      {/* 탭 */}
      <Tabs
        value={tab}
        onChange={setTab}
        items={[
          { key: 'traffic', label: '트래픽' },
          { key: 'timing', label: '매출·트래픽 시점' },
          { key: 'sources', label: '유입 소스' },
          { key: 'behavior', label: '행동' },
        ]}
      />

      {tab === 'traffic' && <TrafficTab data={timeseries.data} error={timeseries.error} />}
      {tab === 'timing' && <TimingTab data={timing.data} error={timing.error} />}
      {tab === 'sources' && <SourcesTab data={sources.data} error={sources.error} />}
      {tab === 'behavior' && <BehaviorTab data={behavior.data} error={behavior.error} />}
    </div>
  )
}

/** KPI 카드 — 로딩 중이면 값 대신 스피너. */
function KpiCard({ label, value, change, loading }: { label: string; value: string; change?: string; loading?: boolean }) {
  if (loading) {
    return (
      <div className={styles.kpiLoadingCard}>
        <p className={styles.kpiLoadingLabel}>{label}</p>
        <Spinner size="sm" />
      </div>
    )
  }
  return <StatCard label={label} value={value} change={change} />
}

function ChartCard({ title, loading, empty, children }: { title: string; loading: boolean; empty: boolean; children: React.ReactNode }) {
  return (
    <Card>
      <p className={styles.cardTitle}>{title}</p>
      <div className={styles.chartBody}>
        {loading ? (
          <div className={styles.center}><Spinner /></div>
        ) : empty ? (
          <Alert variant="info">해당 기간 데이터가 없습니다.</Alert>
        ) : (
          children
        )}
      </div>
    </Card>
  )
}

// ── 트래픽 탭 ──
function TrafficTab({ data, error }: { data: TimeseriesDto | null; error: string | null }) {
  if (error) return <Alert variant="danger">불러오지 못했습니다: {error}</Alert>
  const points = data?.points ?? []
  return (
    <ChartCard title="트래픽 추이 (일별)" loading={!data} empty={points.length === 0}>
      <LineChart
        series={[
          { key: 'sessions', label: '세션', color: CHART_COLORS.primary, data: points.map((p) => ({ date: p.date, value: p.sessions })) },
          { key: 'activeUsers', label: '활성 사용자', color: CHART_COLORS.success, data: points.map((p) => ({ date: p.date, value: p.activeUsers })) },
          { key: 'pageviews', label: '페이지뷰', color: CHART_COLORS.warning, data: points.map((p) => ({ date: p.date, value: p.screenPageViews })) },
        ]}
        showTooltip
        showLegend
        formatTooltip={(pt, ser) => `${ser.label}: ${formatNumber(pt.value)}`}
      />
    </ChartCard>
  )
}

// ── 매출·트래픽 시점 탭 ──
function TimingTab({ data, error }: { data: TimingDto | null; error: string | null }) {
  // 히트맵 셀 드릴다운 드로어 상태 (hooks 는 조기 반환 이전에 선언)
  const [selectedCell, setSelectedCell] = useState<{ dow: number; hour: number } | null>(null)

  if (error) return <Alert variant="danger">불러오지 못했습니다: {error}</Alert>
  if (!data) return <div className={styles.center}><Spinner /></div>

  const revenueEmpty = data.revenue.total === 0
  const trafficEmpty = data.sessions.total === 0

  // marginal 막대용 데이터
  const hourBars = (m: TimingMatrix) => m.hourTotals.map((v, h) => ({ label: `${h}`, value: v }))
  const dayBars = (m: TimingMatrix) => m.dayTotals.map((v, d) => ({ label: DAY_LABELS[d], value: v }))

  const openCell = (dow: number, hour: number) => setSelectedCell({ dow, hour })

  return (
    <div className={styles.tabBody}>
      <Card>
        <p className={styles.cardTitle}>매출 시점 히트맵 (요일 × 시간, ₩)</p>
        {revenueEmpty ? (
          <Alert variant="info">해당 기간 매출 데이터가 없습니다.</Alert>
        ) : (
          <HeatmapGrid
            matrix={data.revenue.matrix}
            color={CHART_COLORS.success}
            formatCell={(day, hour, value) => `${day} ${hour}시 · ${formatCurrency(value)}`}
            onCellClick={openCell}
          />
        )}
      </Card>

      <div className={styles.chartGrid}>
        <ChartCard title="시간대별 매출 (합계)" loading={false} empty={revenueEmpty}>
          <BarChart data={hourBars(data.revenue)} showTooltip formatTooltip={(d) => `${d.label}시: ${formatCurrency(d.value)}`} />
        </ChartCard>
        <ChartCard title="요일별 매출 (합계)" loading={false} empty={revenueEmpty}>
          <BarChart data={dayBars(data.revenue)} showTooltip formatTooltip={(d) => `${d.label}: ${formatCurrency(d.value)}`} />
        </ChartCard>
      </div>

      <Card>
        <p className={styles.cardTitle}>트래픽 시점 히트맵 (요일 × 시간, 세션)</p>
        {trafficEmpty ? (
          <Alert variant="info">해당 기간 트래픽 데이터가 없습니다.</Alert>
        ) : (
          <HeatmapGrid
            matrix={data.sessions.matrix}
            color={CHART_COLORS.primary}
            formatCell={(day, hour, value) => `${day} ${hour}시 · ${formatNumber(value)} 세션`}
            onCellClick={openCell}
          />
        )}
      </Card>

      <div className={styles.chartGrid}>
        <ChartCard title="시간대별 세션 (합계)" loading={false} empty={trafficEmpty}>
          <BarChart data={hourBars(data.sessions)} showTooltip formatTooltip={(d) => `${d.label}시: ${formatNumber(d.value)}`} />
        </ChartCard>
        <ChartCard title="요일별 세션 (합계)" loading={false} empty={trafficEmpty}>
          <BarChart data={dayBars(data.sessions)} showTooltip formatTooltip={(d) => `${d.label}: ${formatNumber(d.value)}`} />
        </ChartCard>
      </div>

      <TimingCellDrawer cell={selectedCell} data={data} onClose={() => setSelectedCell(null)} />
    </div>
  )
}

// ── 유입 소스 탭 ──
const SOURCE_COLUMNS: TableColumn<SourcesDto['sourceMediums'][number]>[] = [
  { key: 'sourceMedium', label: 'Source / Medium' },
  { key: 'sessions', label: '세션', align: 'right', render: (r) => formatNumber(r.sessions) },
  { key: 'keyEvents', label: '전환', align: 'right', render: (r) => formatNumber(r.keyEvents) },
  { key: 'totalRevenue', label: '매출', align: 'right', render: (r) => formatCurrency(r.totalRevenue) },
]

function SourcesTab({ data, error }: { data: SourcesDto | null; error: string | null }) {
  if (error) return <Alert variant="danger">불러오지 못했습니다: {error}</Alert>
  if (!data) return <div className={styles.center}><Spinner /></div>

  const donut = data.channels.map((ch, i) => ({
    label: ch.channel,
    value: ch.sessions,
    color: CHART_PALETTE[i % CHART_PALETTE.length],
  }))

  return (
    <div className={styles.tabBody}>
      <ChartCard title="채널 그룹별 세션 점유" loading={false} empty={data.channels.length === 0}>
        <DonutChart data={donut} showTooltip showLegend formatTooltip={(d, pct) => `${d.label}: ${formatNumber(d.value)} (${pct.toFixed(1)}%)`} />
      </ChartCard>
      <Card>
        <p className={styles.cardTitle}>Source / Medium 상세</p>
        <Table
          size="sm"
          striped
          columns={SOURCE_COLUMNS}
          data={data.sourceMediums}
          rowKey={(r) => r.sourceMedium}
          emptyMessage="해당 기간 유입 데이터가 없습니다."
        />
      </Card>
    </div>
  )
}

// ── 행동 탭 ──
const PAGE_COLUMNS: TableColumn<BehaviorDto['pages'][number]>[] = [
  { key: 'pagePath', label: '페이지' },
  { key: 'screenPageViews', label: '조회수', align: 'right', render: (r) => formatNumber(r.screenPageViews) },
  { key: 'activeUsers', label: '사용자', align: 'right', render: (r) => formatNumber(r.activeUsers) },
  { key: 'userEngagementDuration', label: '참여 시간(초)', align: 'right', render: (r) => formatNumber(Math.round(r.userEngagementDuration)) },
]

function BehaviorTab({ data, error }: { data: BehaviorDto | null; error: string | null }) {
  if (error) return <Alert variant="danger">불러오지 못했습니다: {error}</Alert>
  if (!data) return <div className={styles.center}><Spinner /></div>

  const eventBars = data.events.map((e) => ({ label: e.eventName, value: e.eventCount }))

  return (
    <div className={styles.tabBody}>
      <ChartCard title="상위 이벤트 (발생 수)" loading={false} empty={data.events.length === 0}>
        <BarChart
          data={eventBars}
          direction="horizontal"
          showTooltip
          formatTooltip={(d) => `${d.label}: ${formatNumber(d.value)}`}
        />
      </ChartCard>
      <Card>
        <p className={styles.cardTitle}>상위 페이지</p>
        <Table
          size="sm"
          striped
          columns={PAGE_COLUMNS}
          data={data.pages}
          rowKey={(r) => r.pagePath}
          emptyMessage="해당 기간 페이지 데이터가 없습니다."
        />
      </Card>
    </div>
  )
}
